import type { ParseEntityType } from './enums';

/**
 * Поле → підказка, яку бачить модель.
 *
 * Було просто переліком назв, і десять із них не існували як колонки: `supportEmail`,
 * `supportChat`, сім із девʼяти ігрових і `regulatorName`. Перевірка `allowed.includes()`
 * їх пропускала, а `update` з неіснуючим ключем валився помилкою БД — оператор бачив 500
 * замість доменної відмови, і то вже **після** виклику моделі, тобто заплативши за нього.
 * Тепер ключі звіряються зі схемою прогоном (`parsable-fields.test.ts`).
 *
 * Підказка потрібна там, де назва колонки не каже, чого від моделі хочуть: `rtpBps` — це
 * базисні пункти, і без пояснення модель поверне `96.5`, яке ляже в integer-колонку
 * помилкою типу. Значення пишеться сирим, як його віддала модель, тож єдине місце, де можна
 * домовитись про одиниці, — сам промпт.
 */
export const PARSABLE_FIELD_HINTS: Record<ParseEntityType, Readonly<Record<string, string>>> = {
  brand: {
    legalEntityFooter: 'юридична особа, як її пишуть у футері',
    licenseNumber: 'номер ліцензії',
    originalUrl: 'адреса офіційного сайту',
    signupUrl: 'адреса сторінки реєстрації',
    foundedYear: 'рік заснування, число',
    platform: 'платформа казино',
    holding: 'холдинг або група',
    originalLanguage: 'мова оригінального сайту, код на кшталт en',
    registryUrl: 'посилання на запис у реєстрі регулятора',
    supportEmail: 'e-mail підтримки',
    supportChat: 'посилання на чат підтримки',
  },
  game: {
    releaseYear: 'рік випуску, число',
    volatility: 'волатильність: low, medium, high або very_high',
    rtpBps: 'RTP у базисних пунктах: 96,50% → 9650',
    maxWinMultiplier: 'максимальний виграш у множниках ставки, число',
    linesOrWays: 'лінії або способи виплат, як написано в описі',
    minBet: 'мінімальна ставка в мінорних одиницях: 0,20 EUR → 20',
    maxBet: 'максимальна ставка в мінорних одиницях',
    demoIframeUrl: 'посилання на демо-версію',
  },
  geo: {
    countryName: 'назва країни',
    currencyCode: 'код валюти, три літери',
    minAge: 'мінімальний вік гравця, число',
    payoutTime: 'типовий час виплати',
    kycRequirements: 'вимоги KYC',
    mandatoryDisclaimer: 'обовʼязковий дисклеймер ринку',
    minWithdrawal: 'мінімальна сума виводу в мінорних одиницях',
    maxWithdrawal: 'максимальна сума виводу в мінорних одиницях',
  },
  reference: {
    officialUrl: 'офіційний сайт провайдера',
    foundedYear: 'рік заснування, число',
    gameTypes: 'типи ігор переліком',
  },
  payment_method: {
    processingTime: 'час обробки виплати',
    minDeposit: 'мінімальний депозит у мінорних одиницях',
    maxDeposit: 'максимальний депозит у мінорних одиницях',
    minWithdrawal: 'мінімальний вивід у мінорних одиницях',
    maxWithdrawal: 'максимальний вивід у мінорних одиницях',
    feeBps: 'комісія в базисних пунктах: 2,5% → 250',
    currencies: 'валюти переліком кодів',
  },
  license: {
    jurisdiction: 'юрисдикція регулятора',
    registryUrl: 'адреса публічного реєстру',
    licenseNumberFormat: 'формат номера ліцензії',
    abbrFooter: 'скорочення для футера',
    footerTextTemplate: 'шаблон тексту у футері',
    recognizedGeos: 'ринки, де ліцензію визнають, переліком кодів країн',
    siteRequirements: 'вимоги до сайту переліком',
  },
  rg_organization: {
    helpline: 'телефон гарячої лінії',
    url: 'адреса сайту організації',
    disclaimerOriginal: 'дисклеймер мовою оригіналу',
    minAge: 'мінімальний вік, число',
  },
  /**
   * Порожньо навмисно, а не через недогляд.
   *
   * У `clusters` і `sites` немає описових колонок: усе, що там є, — операційне (домен,
   * статус, `abuse_flag`, привʼязки), і саме від запису в такі поля цей алоулист і захищає.
   * Непорожній перелік тут довелося б набити полями, зміна яких вирішує, що конвеєр зробить
   * із сайтом, — тобто перетворити описовий парсер на керівний.
   */
  cluster: {},
  site: {},
  donor: {
    dr: 'Domain Rating, число',
    traffic: 'місячний трафік, число',
    topics: 'тематики переліком',
    contact: 'контакт для звʼязку',
    spamSignalsPct: 'частка спам-сигналів у відсотках, число',
    price: 'ціна розміщення в мінорних одиницях',
  },
};

/** Назви полів — те, проти чого перевіряються `targets`. */
export const PARSABLE_FIELDS = Object.fromEntries(
  Object.entries(PARSABLE_FIELD_HINTS).map(([entity, hints]) => [entity, Object.keys(hints)]),
) as unknown as Record<ParseEntityType, readonly string[]>;
